import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import SEO from './components/SEO';
import NowPlaying from './components/NowPlaying';
import Home from './pages/Home';
import Music from './pages/Music';
import Tour from './pages/Tour';
import News from './pages/News';
import NewsArticle from './pages/NewsArticle';
import Gallery from './pages/Gallery';
import About from './pages/About';
import Store from './pages/Store';
import ProductDetails from './pages/ProductDetails';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import Unsubscribe from './pages/Unsubscribe';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import CookieConsent from './components/CookieConsent';

// Album data - moved to global scope
const album = {
  title: 'THE KULTS',
  year: '2027',
  tracks: [
    { name: 'Nick of Time', duration: '3:45', isSingle: true },
    { name: "She's Dangerous", duration: '4:02' },
    { name: 'Collide', duration: '3:28' },
    { name: 'What You Mean to Me', duration: '3:55' },
    { name: 'Typewriter Boxer', duration: '4:10', isSingle: true },
    { name: 'Gunslinger', duration: '3:37' },
    { name: "Best You're Gonna Get", duration: '3:50' },
    { name: 'Pushing Me Away', duration: '4:05' },
    { name: 'There For the Taking', duration: '3:33' },
    { name: 'Friend or Foe', duration: '4:20' },
    { name: 'Beatdown', duration: '3:42' },
    { name: 'Duality', duration: '4:15' }
  ]
};

// Component to handle SEO for different routes
const SEOHandler: React.FC = () => {
  const location = useLocation();
  
  const getSEOProps = () => {
    const baseUrl = 'https://thekults.com';
    const baseImage = 'https://thekults.com/social-preview.svg';
    
    switch (location.pathname) {
      case '/':
        return {
          title: 'THE KULTS - Official Website',
          description: 'Official website of THE KULTS. Discover music, tour dates, news, and more.',
          url: baseUrl,
          image: baseImage
        };
      case '/music':
        return {
          title: 'Music - THE KULTS',
          description: 'Listen to THE KULTS music, albums, and latest releases.',
          url: `${baseUrl}/music`,
          image: baseImage
        };
      case '/tour':
        return {
          title: 'Tour Dates - THE KULTS',
          description: 'Check out upcoming tour dates and concert information for THE KULTS.',
          url: `${baseUrl}/tour`,
          image: baseImage
        };
      case '/news':
        return {
          title: 'News - THE KULTS',
          description: 'Latest news, updates, and announcements from THE KULTS.',
          url: `${baseUrl}/news`,
          image: baseImage
        };
      case '/gallery':
        return {
          title: 'Gallery - THE KULTS',
          description: 'Photo and video gallery featuring THE KULTS performances and behind-the-scenes content.',
          url: `${baseUrl}/gallery`,
          image: baseImage
        };
      case '/about':
        return {
          title: 'About - THE KULTS',
          description: 'Learn more about THE KULTS band, members, and story.',
          url: `${baseUrl}/about`,
          image: baseImage
        };
      case '/store':
        return {
          title: 'Store - THE KULTS',
          description: 'Official THE KULTS merchandise, albums, and exclusive items.',
          url: `${baseUrl}/store`,
          image: baseImage
        };
      case '/checkout':
        return {
          title: 'Checkout - THE KULTS Store',
          description: 'Complete your purchase securely.',
          url: `${baseUrl}/checkout`,
          image: baseImage
        };
      case '/contact':
        return {
          title: 'Contact - THE KULTS',
          description: 'Get in touch with THE KULTS management for press inquiries, booking, and business opportunities.',
          url: `${baseUrl}/contact`,
          image: baseImage
        };
      default:
        return {
          title: 'THE KULTS - Official Website',
          description: 'Official website of THE KULTS. Discover music, tour dates, news, and more.',
          url: baseUrl,
          image: baseImage
        };
    }
  };

  const seoProps = getSEOProps();
  
  return <SEO {...seoProps} />;
};

function App() {
  const [playingTrack, setPlayingTrack] = useState<number | null>(null);

  const handlePlayTrack = (trackId: number) => {
    setPlayingTrack(playingTrack === trackId ? null : trackId);
  };

  const handleCloseNowPlaying = () => {
    setPlayingTrack(null);
  };

  return (
    <Router>
      <div className="App">
        <SEOHandler />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/music" element={
              <Music 
                playingTrack={playingTrack}
                onPlayTrack={handlePlayTrack}
                album={album}
              />
            } />
            <Route path="/tour" element={<Tour />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:id" element={<NewsArticle />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/about" element={<About />} />
            <Route path="/store" element={<Store />} />
            <Route path="/product/:id" element={<ProductDetails />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
          </Routes>
        </main>
        <Footer />
        <NowPlaying 
          playingTrack={playingTrack}
          album={album}
          onPlayTrack={handlePlayTrack}
          onClose={handleCloseNowPlaying}
        />
                <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
